import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Plus, X, Layers, Stethoscope, HandHeart, Star, LifeBuoy } from "lucide-react";

const SECTIONS = [
  { key: "diagnosis", title: "Tanı / Durum", icon: Stethoscope, color: "text-blue-600", chip: "bg-blue-50 text-blue-700" },
  { key: "services", title: "Aldığı Hizmetler", icon: HandHeart, color: "text-violet-600", chip: "bg-violet-50 text-violet-700" },
  { key: "strengths", title: "Güçlü Yönler", icon: Star, color: "text-emerald-600", chip: "bg-emerald-50 text-emerald-700" },
  { key: "support_areas", title: "Destek Gerektiren Alanlar", icon: LifeBuoy, color: "text-amber-600", chip: "bg-amber-50 text-amber-700" },
];

function PoolSection({ section, items, onAdd, onDelete }) {
  const [quick, setQuick] = useState("");
  const [bulk, setBulk] = useState("");
  const [importing, setImporting] = useState(false);
  const Icon = section.icon;

  const addQuick = async () => { if (!quick.trim()) return; await onAdd(section.key, [quick]); setQuick(""); };
  const addBulk = async () => {
    const vals = bulk.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
    if (!vals.length) return;
    setImporting(true); await onAdd(section.key, vals); setBulk(""); setImporting(false);
  };

  return (
    <Card className="rounded-2xl border-slate-200/60 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid={`pool-section-${section.key}`}>
      <div className="mb-4 flex items-center gap-2">
        <div className={`flex h-9 w-9 items-center justify-center rounded-xl bg-slate-50 ${section.color}`}><Icon className="h-5 w-5" /></div>
        <h2 className="font-heading text-lg font-bold text-slate-900">{section.title}</h2>
        <span className="ml-auto text-sm text-slate-400">{items.length} öğe</span>
      </div>

      <div className="mb-4 flex min-h-[44px] flex-wrap gap-2">
        {items.map((it) => (
          <span key={it.id} data-testid={`pool-item-${it.id}`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium ${section.chip}`}>
            {it.value}
            <button onClick={() => onDelete(section.key, it.id)} data-testid={`pool-delete-${it.id}`} className="rounded-full p-0.5 hover:bg-black/10"><X className="h-3 w-3" /></button>
          </span>
        ))}
        {items.length === 0 && <p className="text-sm text-slate-400">Henüz öğe yok.</p>}
      </div>

      <div className="flex gap-2">
        <Input value={quick} onChange={(e) => setQuick(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addQuick())}
          placeholder="Hızlı ekle ve Enter'a bas" data-testid={`pool-quick-${section.key}`} className="h-10 rounded-xl" />
        <Button onClick={addQuick} data-testid={`pool-quick-add-${section.key}`} className="h-10 rounded-xl bg-violet-600 text-white hover:bg-violet-700"><Plus className="h-4 w-4" /></Button>
      </div>

      <details className="mt-3">
        <summary className="cursor-pointer text-sm font-medium text-slate-500 hover:text-slate-700">Toplu içe aktar (satır veya virgül)</summary>
        <Textarea value={bulk} onChange={(e) => setBulk(e.target.value)} placeholder="Otizm&#10;Down Sendromu&#10;DEHB" className="mt-2 rounded-xl" data-testid={`pool-bulk-${section.key}`} />
        <Button onClick={addBulk} disabled={importing} data-testid={`pool-bulk-add-${section.key}`} className="mt-2 h-9 rounded-xl bg-slate-800 text-white hover:bg-slate-900">
          {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : "İçe Aktar"}
        </Button>
      </details>
    </Card>
  );
}

export default function Pools() {
  const [pools, setPools] = useState({ diagnosis: [], services: [], strengths: [], support_areas: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const { data } = await api.get("/pools"); setPools(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onAdd = async (category, values) => {
    try { await api.post("/pools", { category, values }); toast.success("Eklendi"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const onDelete = async (category, id) => {
    try { await api.delete(`/pools/${id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="pools-page">
        <header className="mb-6">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.05em] text-slate-500"><Layers className="h-3.5 w-3.5" /> Gelişimsel Profil</p>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">Gelişimsel Profil</h1>
          <p className="mt-2 text-sm text-slate-600">Öğrenci profillerinde kullanılan ortak listeleri buradan yönet. Değişiklikler anında öğrenci formuna yansır.</p>
        </header>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {SECTIONS.map((s) => <PoolSection key={s.key} section={s} items={pools[s.key] || []} onAdd={onAdd} onDelete={onDelete} />)}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
